import { Checkbox, Form, Radio, RadioChangeEvent, Select } from "antd";
import { ReactElement, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import Label from "../../components/label/label.component";
import CustomInput from "../../components/custom-input/custom-input.component";
import "./edit-professional-details.scss";
import TextArea from "antd/es/input/TextArea";
import {
  ADD_TO_BREADCRUMB,
  LOADING,
  REMOVE_FROM_BREADCRUMB,
} from "../../store/actions";
import { useDispatch } from "react-redux";
import { AxiosResponse } from "axios";
import instance from "../../store/axios";
import toast from "react-hot-toast";
import { useForm } from "antd/lib/form/Form";
import CollapseComponent from "../../components/collapse.component";
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import { getStandingDataOptions } from "src/utils/helper/functions";

const EditProfesionalDetails: React.FC = (): ReactElement => {
  const { accountId, activeKyc } = useParams();
  const [form] = useForm();
  Form.useWatch([], form);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isApproveCase = queryParams.get("approve");
  const [accountDetails, setAccountDetails] = useState<any>();
  const [standingData, setStandingData] = useState<any>();

  useEffect(() => {
    async function prepareData() {
      dispatch({ type: LOADING, payload: true });
      await getStandindData();
      await getKycApi();
      dispatch({ type: LOADING, payload: false });
    }

    prepareData();

    dispatch({
      type: ADD_TO_BREADCRUMB,
      payload: {
        breadcrumb: [
          {
            text: `Update professional details`,
            type: "text",
          },
        ],
      },
    });

    return () => {
      dispatch({
        type: REMOVE_FROM_BREADCRUMB,
        payload: {
          breadcrumb: [
            {
              text: `Update professional details`,
            },
          ],
        },
      });
    };
  }, []);

  const getKycApi = async (): Promise<void> => {
    try {
      const response: AxiosResponse<any> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}?kycId=${activeKyc}`
      );
      setAccountDetails(response.data.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/professional-details/${accountId}/${activeKyc}/edit`
      );
    }
  };

  const getStandindData = async (): Promise<void> => {
    try {
      const response: AxiosResponse<any> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/option/standing-data`
      );
      setStandingData(response.data.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/professional-details/${accountId}/${activeKyc}/edit`
      );
    }
  };

  const isPoliticalPerson = (): boolean => {
    if (
      accountDetails?.accountInfo[0]?.pep1 ||
      accountDetails?.accountInfo[0]?.pep2 ||
      accountDetails?.accountInfo[0]?.pep3 ||
      accountDetails?.accountInfo[0]?.pep4 ||
      accountDetails?.accountInfo[0]?.pep5 ||
      accountDetails?.accountInfo[0]?.pep7
    ) {
      return true;
    }

    return false;
  };

  const getIsDealingValues = (): string[] => {
    const array: string[] = [];
    if (accountDetails?.accountInfo[0]?.isDealingInSecurities) {
      array.push("isDealingInSecurities");
    }
    if (accountDetails?.accountInfo[0]?.isFundsInKamco) {
      array.push("isFundsInKamco");
    }
    if (accountDetails?.accountInfo[0]?.isWorkInFinancial) {
      array.push("isWorkInFinancial");
    }
    return array;
  };

  const getPepsValue = (): string[] => {
    const array: string[] = [];
    for (let i = 1; i <= 7; i++) {
      if (
        accountDetails?.accountInfo[0] &&
        accountDetails?.accountInfo[0][`pep${i}`]
      ) {
        array.push(`pep${i}`);
      }
    }
    return array;
  };

  const onSubmit = async (values: any) => {
    dispatch({ type: LOADING, payload: true });

    values.kycId = accountDetails.accountInfo[0]?.id;
    values.isRetailClient = values.classification !== 41 ? true : false;
    values.qualifiedProfessionalClient =
      values.classification === 41 ? true : false;
    values.branch = "KW";
    delete values.classification;

    for (let i = 1; i <= 7; i++) {
      if (values.politicalPerson) {
        values.pep8 = false;
        values?.peps?.includes(`pep${i}`)
          ? (values[`pep${i}`] = true)
          : (values[`pep${i}`] = false);
      } else {
        values.pep8 = true;
        values[`pep${i}`] = false;
      }
    }
    delete values.peps;
    values.isDealingInSecurities = false;
    values.isFundsInKamco = false;
    values.isWorkInFinancial = false;

    if (values.isDealing?.includes("isDealingInSecurities")) {
      values.isDealingInSecurities = true;
    }
    if (values.isDealing?.includes("isFundsInKamco")) {
      values.isFundsInKamco = true;
    }
    if (values.isDealing?.includes("isWorkInFinancial")) {
      values.isWorkInFinancial = true;
    }

    delete values.isDealing;

    try {
      await instance.post(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}/professional/update`,
        values
      );
      toast.success("Professional information updated successfully");
      navigate(
        `/home/business-account/${accountId}/${activeKyc}/details?tab=4${isApproveCase ? "&approve=1" : ""
        }`
      );
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/professional-details/${accountId}/${activeKyc}/edit${isApproveCase ? "?approve=1" : ""
        }`
      );
    }

    dispatch({ type: LOADING, payload: false });
  };

  return (
    <div className="edit-professional-details-container flex flex-col">
      <BreadcrumbComponent />
      <h2 className=" font-semibold text-xl">Professional information</h2>
      {accountDetails?.id && (
        <Form
          className="add-edit-form"
          initialValues={{
            employmentStatusId:
              accountDetails?.accountInfo[0]?.employmentStatusId,
            employerName: accountDetails?.accountInfo[0]?.employerName,
            occupationTitle: accountDetails?.accountInfo[0]?.occupationTitle,
            retirementAddress:
              accountDetails?.accountInfo[0]?.retirementAddress,
            workNature: accountDetails?.accountInfo[0]?.workNature,
            classification: accountDetails?.accountInfo[0]
              ?.qualifiedProfessionalClient
              ? 41
              : 40,
            educationLevelId: accountDetails?.accountInfo[0]?.educationLevelId,
            educationLevelOther:
              accountDetails?.accountInfo[0]?.educationLevelOther,
            isDealing: getIsDealingValues(),
            politicalPerson: isPoliticalPerson(),
            isBoardMember: accountDetails?.accountInfo[0]?.isBoardMember,
            boardMemberDetails:
              accountDetails?.accountInfo[0]?.boardMemberDetails,
            categories: !accountDetails?.accountInfo[0]?.pep8,
            peps: getPepsValue(),
            pepDetails: accountDetails?.accountInfo[0]?.pepDetails,
          }}
          form={form}
          onFinish={onSubmit}
        >
          <CollapseComponent
            header={
              <div className="flex gap-3 items-center">
                <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                <h3 className=" text-lg font-semibold">Work details</h3>
              </div>
            }
          >
            <div className="fields flex justify-space-between wrap">
              <div className="field flex column">
                <Label text="Current Job" />
                <Form.Item
                  name="employmentStatusId"
                  rules={[
                    {
                      required: true,
                      message: "Please select the current job!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select current job"

                    options={[
                      ...getStandingDataOptions(
                        standingData,
                        "employment",
                        ["kw", "gc"],
                        true
                      ),
                    ]}
                  />
                </Form.Item>
              </div>

              {form.getFieldValue("employmentStatusId") == -1 && (
                <>
                  <div className="field flex flex-col">
                    <Label text="Nature of work" />
                    <Form.Item
                      name="workNature"
                      rules={[
                        {
                          required:
                            form.getFieldValue("employmentStatusId") == -1
                              ? true
                              : false,
                          message: "Please input nature of work!",
                        },
                      ]}
                    >
                      <CustomInput placeholder="Enter nature of work" />
                    </Form.Item>
                  </div>
                </>
              )}

              {form.getFieldValue("employmentStatusId") == 35 && (
                <>
                  <div className="field flex flex-col">
                    <Label text="Employer Name" />
                    <Form.Item
                      name="employerName"
                      rules={[
                        {
                          required:
                            form.getFieldValue("employmentStatusId") == 35
                              ? true
                              : false,
                          message: "Please input employer name!",
                        },
                        {
                          max: 60,
                          message: "Maximum 60 characters allowed",
                        },
                      ]}
                    >
                      <CustomInput placeholder="Enter employer name" />
                    </Form.Item>
                  </div>
                  <div className="field flex column">
                    <Label text="Occupation & Title" />
                    <Form.Item
                      name="occupationTitle"
                      rules={[
                        {
                          required:
                            form.getFieldValue("employmentStatusId") == 35
                              ? true
                              : false,
                          message: "Please input occupation & title!",
                        },
                        {
                          max: 50,
                          message: "Maximum 50 characters allowed",
                        },
                      ]}
                    >
                      <CustomInput placeholder="Enter occupation & title" />
                    </Form.Item>
                  </div>
                </>
              )}

              {form.getFieldValue("employmentStatusId") == 33 && (
                <>
                  <div className="field flex flex-col">
                    <Label text="Previous Employer Name" />
                    <Form.Item
                      name="employerName"
                      rules={[
                        {
                          required:
                            form.getFieldValue("employmentStatusId") == 33
                              ? true
                              : false,
                          message: "Please input previous employer name!",
                        },
                      ]}
                    >
                      <CustomInput placeholder="Enter previous employer name" />
                    </Form.Item>
                  </div>
                  <div className="field flex flex-col">
                    <Label text="Previous Occupation & Title" />
                    <Form.Item
                      name="occupationTitle"
                      rules={[
                        {
                          required:
                            form.getFieldValue("employmentStatusId") == 33
                              ? true
                              : false,
                          message: "Please input previous occupation & title!",
                        },
                      ]}
                    >
                      <CustomInput placeholder="Enter previous occupation & title" />
                    </Form.Item>
                  </div>
                  <div className="field flex flex-col">
                    <Label text="Previous Work Address" />
                    <Form.Item
                      name="retirementAddress"
                      rules={[
                        {
                          required:
                            form.getFieldValue("employmentStatusId") == 33
                              ? true
                              : false,
                          message: "Please input previous work address!",
                        },
                      ]}
                    >
                      <CustomInput placeholder="Enter previous work address" />
                    </Form.Item>
                  </div>
                </>
              )}

              {form.getFieldValue("employmentStatusId") == 34 && (
                <div className="field flex column">
                  <Label text="Nature of Work" />
                  <Form.Item
                    name="workNature"
                    rules={[
                      {
                        required:
                          form.getFieldValue("employmentStatusId") == 34
                            ? true
                            : false,
                        message: "Please input nature of work!",
                      },
                    ]}
                  >
                    <CustomInput placeholder="Enter nature of work" />
                  </Form.Item>
                </div>
              )}
            </div>
          </CollapseComponent>

          <CollapseComponent
            header={
              <div className="flex gap-3 items-center">
                <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                <h3 className=" text-lg font-semibold">
                  Client Classification
                </h3>
              </div>
            }
          >
            <div className="fields flex justify-space-between wrap">
              <div className="field flex column">
                <Label text="Choose a classification" />
                <Form.Item
                  name="classification"
                  rules={[
                    {
                      required: true,
                      message: "Please choose a classification!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select classification"

                    options={[
                      ...getStandingDataOptions(
                        standingData,
                        "clientClassification",
                        ["kw", "gc"],
                        false
                      ),
                    ]}
                  />
                </Form.Item>
              </div>

              {form.getFieldValue("classification") == 41 && (
                <div className="fields flex column wrap">
                  <div className="fields flex column">
                    <Label text="Choose all possible options" />
                    <Form.Item
                      name="isDealing"
                      rules={[
                        {
                          required: true,
                          message: "Please select at least one option!",
                        },
                      ]}
                    >
                      <Checkbox.Group
                        options={[
                          {
                            label:
                              "I have dealings with securities of not less than KWD 250,000 every quarter during the previous two years",
                            value: "isDealingInSecurities",
                          },
                          {
                            label:
                              "I have funds and assets with Kamco Investment Company, Other licensed companies, or both not less than KWD 100,000",
                            value: "isFundsInKamco",
                          },
                          {
                            label:
                              "I have worked in the financial sector for at least one year in a professional position",
                            value: "isWorkInFinancial",
                          },
                        ]}
                      ></Checkbox.Group>
                    </Form.Item>
                  </div>
                </div>
              )}
            </div>
          </CollapseComponent>
          <CollapseComponent
            header={
              <div className="flex gap-3 items-center">
                <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                <h3 className=" text-lg font-semibold">Other information</h3>
              </div>
            }
          >
            <div className="fields flex justify-space-between wrap">
              <div className="fields flex column wrap">
                <div className="fields flex wrap justify-space-between">
                  <div className="field flex column">
                    <Label text="Education Level" />
                    <Form.Item
                      name="educationLevelId"
                      rules={[
                        {
                          required: true,
                          message: "Please input education level!",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select education level"

                        options={[
                          ...getStandingDataOptions(
                            standingData,
                            "education",
                            ["kw", "gc"],
                            true
                          ),
                        ]}
                      />
                    </Form.Item>
                  </div>

                  {form.getFieldValue("educationLevelId") == -1 && (
                    <>
                      <div className="field flex flex-col">
                        <Label text="Other education level" />
                        <Form.Item
                          name="educationLevelOther"
                          rules={[
                            {
                              required:
                                form.getFieldValue("educationLevelId") == -1
                                  ? true
                                  : false,
                              message: "Please input nature of work!",
                            },
                          ]}
                        >
                          <CustomInput placeholder="Enter other education level" />
                        </Form.Item>
                      </div>
                    </>
                  )}

                  <div className="field flex column">
                    <Label text="Are you a Politically Exposed Person?" />
                    <Form.Item
                      name="politicalPerson"
                      rules={[
                        {
                          required: true,
                          message:
                            "Please select Are you a Politically Exposed Person?",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Are you a Politically Exposed Person?"
                        options={[
                          { value: true, label: "Yes" },
                          { value: false, label: "No" },
                        ]}
                      />
                    </Form.Item>
                  </div>

                  {form.getFieldValue("politicalPerson") && (
                    <div className="fields flex flex-col">
                      <Label text="If you belong to any of the following categories, please select and give details below:" />
                      <Form.Item
                        name="peps"
                        rules={[
                          {
                            required: form.getFieldValue("politicalPerson")
                              ? true
                              : false,
                            message: "Please select at leaser one category!",
                          },
                        ]}
                      >
                        <Checkbox.Group
                          className="flex flex-col"
                          options={[
                            {
                              label:
                                "Judge or member of an official legal council",
                              value: "pep1",
                            },
                            {
                              label:
                                "Close family member (first and second degree) or associate of the persons above mentioned",
                              value: "pep2",
                            },
                            {
                              label:
                                "Senior executive for a state owned corporation / organization",
                              value: "pep3",
                            },
                            {
                              label: "Senior military official",
                              value: "pep4",
                            },
                            {
                              label:
                                "Head of state / government / member of ruling family",
                              value: "pep5",
                            },
                            {
                              label:
                                "Senior politician / minister / member of parliament",
                              value: "pep6",
                            },
                            {
                              label: "Senior government official",
                              value: "pep7",
                            },
                          ]}
                        ></Checkbox.Group>
                      </Form.Item>
                    </div>
                  )}

                  {form.getFieldValue("politicalPerson") && (
                    <div className="fields flex flex-col">
                      <Label text="Details" />
                      <Form.Item
                        name="pepDetails"
                        rules={[
                          {
                            required: true,
                            message: "Please enter the details!",
                          },
                        ]}
                      >
                        <TextArea rows={4} placeholder="Enter details" />
                      </Form.Item>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="fields flex justify-space-between wrap">
              <div className="field flex column">
                <Label text="Are you a member of the board of directors or member of the executive management or an insider of a listed company?" />
                <Form.Item
                  name="isBoardMember"
                  rules={[
                    {
                      required: true,
                      message: "Please choose!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select"
                    options={[
                      { value: true, label: "Yes" },
                      { value: false, label: "No" },
                    ]}
                  />
                </Form.Item>
              </div>

              {form.getFieldValue("isBoardMember") && (
                <div className="fields flex column">
                  <Label text="Details" />
                  <Form.Item
                    name="boardMemberDetails"
                    rules={[
                      {
                        required: true,
                        message: "Please enter the board member details!",
                      },
                    ]}
                  >
                    <TextArea
                      // value={accountDetails?.accountInfo[0].boardMemberDetails}
                      rows={4}
                      placeholder="Enter details"
                    />
                  </Form.Item>
                </div>
              )}
            </div>
          </CollapseComponent>
          <div className="flex w-full flex-wrap items-center justify-end mt-7 pt-5">
            <div className="flex justify-between items-center w-[70%] md:w-[48%]">
              <button
                onClick={() =>
                  navigate(
                    `/home/business-account/${accountId}/${activeKyc}/details?tab=4${isApproveCase ? "&approve=1" : ""
                    }`
                  )
                }
                className="rounded-lg bg-white w-[48%] py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
              >
                Cancel
              </button>
              <button className="disabled:opacity-50 disabled:cursor-not-allowed rounded-lg bg-blue-500 w-[48%] py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-white">
                Save
              </button>
            </div>
          </div>
        </Form>
      )}
    </div>
  );
};

export default EditProfesionalDetails;
